import React, { useState, useCallback, useRef, useEffect } from 'react';
import apiService from '../../../services/api';

function getFreightApiBase() {
  // Freight API base: https://freight.bsbfortrade.com → /api/quote, /api/web/freight-request, etc.
  const env = (process.env.REACT_APP_FREIGHT_API_URL || process.env.REACT_APP_API_URL || '')
    .toString()
    .replace(/\/$/, '');
  if (env) return env;
  return 'https://freight.bsbfortrade.com';
}

const QUANTITY_MIN = 10;
const QUANTITY_MAX = 45;
const PINCODE_LIST_DEBOUNCE_MS = 350;
const MIN_SEARCH_LEN = 2;
const PAGE_SIZE = 10;

function formatRowLabel(row) {
  if (!row) return '';
  const parts = [row.name, row.pincode, row.city_name, row.state_name].filter(Boolean);
  return parts.join(' · ');
}

function LocationField({
  id,
  label,
  placeholder,
  inputValue,
  onInputChange,
  list,
  listLoading,
  open,
  onOpenChange,
  onSelectRow,
  selected,
  containerRef,
  readOnly,
}) {
  return (
    <div className="form-field-mini" ref={containerRef} style={{ position: 'relative' }}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        required={!readOnly}
        readOnly={readOnly}
        autoComplete="off"
        placeholder={readOnly ? '' : placeholder}
        value={inputValue}
        style={readOnly ? { backgroundColor: '#f5f5f5' } : undefined}
        onChange={(e) => {
          if (readOnly) return;
          onInputChange(e.target.value);
          onOpenChange(true);
        }}
        onFocus={() => !readOnly && onOpenChange(true)}
      />
      {!readOnly &&
        open &&
        (listLoading ||
          (list && list.length > 0) ||
          (!listLoading && inputValue.trim().length >= MIN_SEARCH_LEN)) && (
          <ul
            role="listbox"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              margin: '4px 0 0',
              padding: 0,
              listStyle: 'none',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: 6,
              boxShadow: '0 8px 24px rgba(0,0,0,.12)',
              maxHeight: 220,
              overflowY: 'auto',
              zIndex: 50,
            }}
          >
            {listLoading && (
              <li style={{ padding: '10px 12px', color: '#666', fontSize: 13 }}>Searching…</li>
            )}
            {!listLoading && list && list.length === 0 && inputValue.trim().length >= MIN_SEARCH_LEN && (
              <li style={{ padding: '10px 12px', color: '#888', fontSize: 13 }}>
                No matches — try pincode or other spelling
              </li>
            )}
            {!listLoading &&
              list &&
              list.map((row) => (
                <li
                  key={`${row.area_id}-${row.pincode}-${row.name}`}
                  role="option"
                  aria-selected={false}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                    borderBottom: '1px solid #eee',
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelectRow(row)}
                >
                  <div style={{ fontWeight: 600 }}>{row.name}</div>
                  <div style={{ color: '#555', marginTop: 2 }}>
                    {row.pincode}
                    {row.city_name ? ` · ${row.city_name}` : ''}
                    {row.state_name ? `, ${row.state_name}` : ''}
                  </div>
                </li>
              ))}
          </ul>
        )}
      {selected && !readOnly && (
        <small style={{ display: 'block', marginTop: 4, color: '#2d6a4f', fontSize: 12 }}>
          Selected: {selected.pincode}
          {selected.area_id != null ? ` (area ${selected.area_id})` : ''}
        </small>
      )}
    </div>
  );
}

const FreightCalculatorModule = () => {
  const baseUrl = getFreightApiBase();
  const api = `${baseUrl}/api/quote`;

  const [step, setStep] = useState(1);
  const [loadInput, setLoadInput] = useState('');
  const [unloadInput, setUnloadInput] = useState('');
  const [loadSelected, setLoadSelected] = useState(null);
  const [unloadSelected, setUnloadSelected] = useState(null);
  const [loadList, setLoadList] = useState([]);
  const [unloadList, setUnloadList] = useState([]);
  const [loadListLoading, setLoadListLoading] = useState(false);
  const [unloadListLoading, setUnloadListLoading] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [unloadOpen, setUnloadOpen] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [freightRate, setFreightRate] = useState(null);
  /** Body for POST /api/quote after step 1 validated */
  const [pendingBody, setPendingBody] = useState(null);

  const loadDebounceRef = useRef(null);
  const unloadDebounceRef = useRef(null);
  const loadWrapRef = useRef(null);
  const unloadWrapRef = useRef(null);

  const fetchPincodeList = useCallback(async (searchText) => {
    const res = await fetch(api, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        intent: 'pincode_list',
        search_text: searchText,
        page_size: PAGE_SIZE,
      }),
    });
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      return [];
    }
    if (!data.success || !Array.isArray(data.data)) return [];
    return data.data;
  }, [api]);

  useEffect(() => {
    if (step !== 1) return;
    if (loadDebounceRef.current) clearTimeout(loadDebounceRef.current);
    const q = loadInput.trim();
    if (q.length < MIN_SEARCH_LEN) {
      setLoadList([]);
      setLoadListLoading(false);
      return;
    }
    setLoadListLoading(true);
    loadDebounceRef.current = setTimeout(async () => {
      try {
        setLoadList(await fetchPincodeList(q));
      } catch {
        setLoadList([]);
      } finally {
        setLoadListLoading(false);
      }
    }, PINCODE_LIST_DEBOUNCE_MS);
    return () => {
      if (loadDebounceRef.current) clearTimeout(loadDebounceRef.current);
    };
  }, [loadInput, fetchPincodeList, step]);

  useEffect(() => {
    if (step !== 1) return;
    if (unloadDebounceRef.current) clearTimeout(unloadDebounceRef.current);
    const q = unloadInput.trim();
    if (q.length < MIN_SEARCH_LEN) {
      setUnloadList([]);
      setUnloadListLoading(false);
      return;
    }
    setUnloadListLoading(true);
    unloadDebounceRef.current = setTimeout(async () => {
      try {
        setUnloadList(await fetchPincodeList(q));
      } catch {
        setUnloadList([]);
      } finally {
        setUnloadListLoading(false);
      }
    }, PINCODE_LIST_DEBOUNCE_MS);
    return () => {
      if (unloadDebounceRef.current) clearTimeout(unloadDebounceRef.current);
    };
  }, [unloadInput, fetchPincodeList, step]);

  useEffect(() => {
    const close = (e) => {
      if (loadWrapRef.current && !loadWrapRef.current.contains(e.target)) setLoadOpen(false);
      if (unloadWrapRef.current && !unloadWrapRef.current.contains(e.target)) setUnloadOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const validateQuantity = useCallback((raw) => {
    const n = Number(raw);
    if (raw === '' || Number.isNaN(n)) {
      return { ok: false, message: 'Enter quantity (MT).' };
    }
    if (n < QUANTITY_MIN || n > QUANTITY_MAX) {
      return {
        ok: false,
        message: `Quantity must be between ${QUANTITY_MIN} and ${QUANTITY_MAX} MT.`,
      };
    }
    return { ok: true, value: n };
  }, []);

  const onQuantityChange = (e) => {
    const v = e.target.value;
    if (v === '') {
      setQuantity('');
      return;
    }
    if (/^\d*\.?\d*$/.test(v)) setQuantity(v);
  };

  const onSelectLoad = (row) => {
    setLoadSelected({
      pincode: String(row.pincode || '').trim(),
      area_id: row.area_id,
      name: row.name,
    });
    setLoadInput(formatRowLabel(row));
    setLoadOpen(false);
  };

  const onSelectUnload = (row) => {
    setUnloadSelected({
      pincode: String(row.pincode || '').trim(),
      area_id: row.area_id,
      name: row.name,
    });
    setUnloadInput(formatRowLabel(row));
    setUnloadOpen(false);
  };

  const goToStep2 = (e) => {
    e.preventDefault();
    setError('');
    const loadTrim = loadInput.trim();
    const unloadTrim = unloadInput.trim();
    if (!loadTrim || !unloadTrim) {
      setError('Enter loading and unloading location (or 6-digit pincode).');
      return;
    }
    const q = validateQuantity(quantity);
    if (!q.ok) {
      setError(q.message);
      return;
    }
    const loading =
      loadSelected?.pincode || (/^\d{6}$/.test(loadTrim) ? loadTrim : loadTrim);
    const unloading =
      unloadSelected?.pincode || (/^\d{6}$/.test(unloadTrim) ? unloadTrim : unloadTrim);
    if (!loading || !unloading) {
      setError('Set both locations.');
      return;
    }
    const body = { loading, unloading, quantity: q.value };
    if (loadSelected?.area_id != null) body.loading_area_id = Number(loadSelected.area_id);
    if (unloadSelected?.area_id != null) body.unloading_area_id = Number(unloadSelected.area_id);
    setPendingBody(body);
    setStep(2);
  };

  const submitMobileAndGetRate = async () => {
    const m = mobile.replace(/\D/g, '').slice(0, 10);
    if (m.length !== 10) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!pendingBody) {
      setStep(1);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingBody),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setError('Invalid response from server.');
        return;
      }
      if (!data.success || data.intent === 'pincode_list') {
        setError(data.message || 'Could not calculate freight.');
        return;
      }
      const rate = Number(data.rate);
      setFreightRate(rate);
      setMobile(m);
      try {
        const fd = new FormData();
        fd.append('pickup_location', loadInput);
        fd.append('drop_location', unloadInput);
        fd.append('quantity', String(pendingBody.quantity));
        fd.append('mobile', m);
        fd.append('freight_amount', String(rate));
        await apiService.submitFreightRequest(fd);
      } catch {
        /* optional lead save */
      }
      setStep(3);
    } catch (err) {
      console.error(err);
      setError('Network error. Check API / proxy.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setLoadInput('');
    setUnloadInput('');
    setLoadSelected(null);
    setUnloadSelected(null);
    setQuantity('');
    setMobile('');
    setFreightRate(null);
    setPendingBody(null);
    setError('');
  };

  return (
    <div className="module-card logistics-module">
      <div className="module-header">
        <div className="module-icon">
          <i className="fas fa-truck-moving"></i>
        </div>
        <h3>Freight Calculator</h3>
      </div>
      <div
        className="calculator-mini-form freight-flow"
        style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}
      >
        {step === 1 && (
          <form
            onSubmit={goToStep2}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: '100%',
              flex: 1,
              minHeight: 0,
            }}
          >
            <LocationField
              id="freight-loading"
              label="Loading location (Pickup)"
              placeholder="Enter loading location"
              inputValue={loadInput}
              onInputChange={(v) => {
                setLoadInput(v);
                setLoadSelected(null);
              }}
              list={loadList}
              listLoading={loadListLoading}
              open={loadOpen}
              onOpenChange={setLoadOpen}
              onSelectRow={onSelectLoad}
              selected={loadSelected}
              containerRef={loadWrapRef}
            />
            <LocationField
              id="freight-unloading"
              label="Unloading location (Drop)"
              placeholder="Enter unloading location"
              inputValue={unloadInput}
              onInputChange={(v) => {
                setUnloadInput(v);
                setUnloadSelected(null);
              }}
              list={unloadList}
              listLoading={unloadListLoading}
              open={unloadOpen}
              onOpenChange={setUnloadOpen}
              onSelectRow={onSelectUnload}
              selected={unloadSelected}
              containerRef={unloadWrapRef}
            />
            <div className="form-field-mini">
              <label htmlFor="freight-quantity">Quantity (MT)</label>
              <input
                id="freight-quantity"
                type="text"
                required
                inputMode="decimal"
                placeholder="10–45"
                value={quantity}
                onChange={onQuantityChange}
              />
            </div>
            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: 6,
                  color: '#721c24',
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary btn-calculate"
              style={{ width: '100%', marginTop: 'auto' }}
            >
              Calculate Freight
            </button>
          </form>
        )}

        {step === 2 && pendingBody && (
          <div
            className="credit-step active"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: '100%',
              flex: 1,
            }}
          >
            <LocationField
              id="freight-loading-ro"
              label="Loading location (Pickup)"
              placeholder=""
              inputValue={loadInput}
              onInputChange={() => {}}
              list={[]}
              listLoading={false}
              open={false}
              onOpenChange={() => {}}
              onSelectRow={() => {}}
              selected={null}
              containerRef={loadWrapRef}
              readOnly
            />
            <LocationField
              id="freight-unloading-ro"
              label="Unloading location (Drop)"
              placeholder=""
              inputValue={unloadInput}
              onInputChange={() => {}}
              list={[]}
              listLoading={false}
              open={false}
              onOpenChange={() => {}}
              onSelectRow={() => {}}
              selected={null}
              containerRef={unloadWrapRef}
              readOnly
            />
            <div className="form-field-mini">
              <label>Quantity (MT)</label>
              <input type="text" readOnly value={quantity} style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Mobile Number</label>
              <input
                type="tel"
                name="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter mobile number"
                maxLength={10}
                inputMode="numeric"
                required
              />
            </div>
            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  background: '#f8d7da',
                  borderRadius: 6,
                  color: '#721c24',
                  fontSize: 14,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary btn-calculate"
              disabled={submitting || mobile.replace(/\D/g, '').length !== 10}
              onClick={submitMobileAndGetRate}
              style={{ width: '100%', marginTop: 'auto' }}
            >
              {submitting ? 'Calculating…' : 'Submit'}
            </button>
          </div>
        )}

        {step === 3 && freightRate != null && (
          <div
            className="credit-step active"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: '100%',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <div className="form-field-mini">
              <label>Loading location (Pickup)</label>
              <input type="text" readOnly value={loadInput} style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Unloading location (Drop)</label>
              <input type="text" readOnly value={unloadInput} style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Quantity (MT)</label>
              <input type="text" readOnly value={quantity} style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div className="form-field-mini">
              <label>Mobile Number</label>
              <input type="text" readOnly value={mobile} style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div
              className="calculator-mini-result"
              id="freight-total-result-box"
              aria-live="polite"
            >
              <span className="result-label">TOTAL FREIGHT</span>
              <span className="result-value" id="freight-total-amount">
                ₹{freightRate.toLocaleString('en-IN')}
              </span>
              <span className="result-label freight-total-sub" style={{ marginBottom: 0 }}>
                Estimated rate for this route &amp; quantity
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              style={{ marginTop: 'auto' }}
            >
              Calculate Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreightCalculatorModule;
